import React from "react";
import { Link as ScrollLink } from "react-scroll";
import { useReducedMotion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";
import { Band, SectionHead } from "../ui/Section";
import Reveal from "../ui/Reveal";
import Icon from "../ui/Icon";
import VideoFrame from "../ui/VideoFrame";

const MANAGEMENT_URL = "https://management.cosebu.com/";
const TOUR_VIDEO = "VYFWlNunP8A";

const Home = () => {
  const { t } = useLanguage();
  const reduced = useReducedMotion();

  const stats = [
    { value: "110", label: t.home.statSellers },
    { value: "45,000", label: t.home.statMarket },
    { value: "80%", label: t.home.statExcluded },
  ];

  const context = [
    {
      icon: "store",
      kicker: t.home.problemLabel,
      title: t.home.problemsSellersTitle,
      text: t.home.problemsSellersText,
    },
    {
      icon: "cart",
      kicker: t.home.problemLabel,
      title: t.home.problemsBuyersTitle,
      text: t.home.problemsBuyersText,
    },
    {
      icon: "target",
      kicker: t.home.directionLabel,
      title: t.home.mission,
      text: t.home.missionDesc,
    },
    {
      icon: "compass",
      kicker: t.home.directionLabel,
      title: t.home.vision,
      text: t.home.visionDesc,
    },
  ];

  return (
    <>
      {/* ---------- Hero ------------------------------------------------ */}
      <section id="home" className="band band--flush pb-0 pt-14 md:pt-20">
        <div className="shell">
          <div className="grid items-end gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="tag tag--accent">
                  <span className="dot" />
                  {t.home.kicker}
                </span>
              </Reveal>

              <Reveal delay={0.04}>
                <h1 className="display mt-6 max-w-[16ch]">{t.home.title}</h1>
              </Reveal>

              <Reveal delay={0.08}>
                <p className="subhead mt-6 max-w-[52ch]">
                  {t.home.standfirst}
                </p>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <a
                    href={MANAGEMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn--primary btn--lg"
                  >
                    {t.home.openPlatform}
                    <Icon name="arrowUpRight" size={16} />
                  </a>
                  <ScrollLink
                    to="management"
                    smooth
                    offset={-72}
                    duration={reduced ? 0 : 420}
                    className="btn btn--secondary btn--lg cursor-pointer"
                  >
                    {t.home.seeHowItWorks}
                  </ScrollLink>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.16}>
                <p className="eyebrow mb-3">{t.home.demoLabel}</p>
                <VideoFrame id={TOUR_VIDEO} title={t.home.demoTitle} />
                <p className="meta mt-3">{t.home.demoCaption}</p>
              </Reveal>
            </div>
          </div>

          {/* Metrics strip: hairline-separated, mono figures. */}
          <Reveal delay={0.2}>
            <div className="mt-14 grid grid-cols-1 border-t border-line sm:grid-cols-3 md:mt-20">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={[
                    "flex items-baseline gap-4 py-5 sm:block sm:py-7",
                    index > 0 ? "border-t border-line sm:border-l sm:border-t-0 sm:pl-7" : "",
                  ].join(" ")}
                >
                  <p className="num text-[28px] leading-none md:text-[34px]">
                    {stat.value}
                  </p>
                  <p className="meta sm:mt-2.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Context --------------------------------------------- */}
      <Band>
        <SectionHead index={1} label={t.home.contextLabel} title={t.home.movementTitle}>
          {t.home.movementDesc}
        </SectionHead>

        <div className="rail">
          <div className="rail__label" aria-hidden="true" />
          <div className="grid gap-x-14 sm:grid-cols-2 sm:border-t sm:border-line">
            {context.map((item, index) => (
              <Reveal
                key={item.title}
                delay={index * 0.04}
                className="border-t border-line py-8 sm:[&:nth-child(-n+2)]:border-t-0"
              >
                <div className="flex items-center gap-3">
                  <Icon name={item.icon} size={18} className="text-clay" />
                  <span className="eyebrow">{item.kicker}</span>
                </div>
                <h3 className="mt-4 text-[19px] font-semibold tracking-[-0.01em]">
                  {item.title}
                </h3>
                <p className="meta mt-3 max-w-[46ch] text-[14px]">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Band>
    </>
  );
};

export default Home;
